<script>
// Untranslated names already reported, shared by every Icon so each name is logged once per page load.
const reported = new Set();
</script>

<script setup>
import { cloneVNode, h, isVNode, useAttrs } from 'vue';
import { carbonIconFor, hasCarbonIcons } from './iconRegistry';

// An icon by name, as the app has always passed it ("i-lucide-plus"). Names registered in iconRegistry (the dashboard
// and the v3 login register carbonMap's CARBON_ICONS) draw the Polaris kit's Carbon icon; the others, and every name in
// a bundle that registers nothing (widget, portal, survey), keep the Iconify class. A Carbon icon follows the text size
// (1em) unless the caller sizes it, as the Iconify ones did. Either way the element carries data-icon="<name>".
const props = defineProps({
  icon: { type: [String, Object, Function], required: true },
});

defineOptions({ inheritAttrs: false });
const attrs = useAttrs();

const SIZED = /(^|\s|!)(size|w|h)-/;

const renderIcon = () => {
  if (!props.icon) return null;
  // A VNode or a component (the filter operators' `h('span', …)`) keeps the caller's classes, as fallthrough did.
  if (isVNode(props.icon)) return cloneVNode(props.icon, attrs, true);
  if (typeof props.icon === 'function') return h(props.icon, attrs);
  const carbon = carbonIconFor(props.icon);
  if (carbon) {
    const callerClass = [attrs.class].flat().filter(Boolean).join(' ');
    return h(carbon, {
      ...attrs,
      class: [attrs.class, !SIZED.test(callerClass) && 'size-[1em]'],
      'aria-hidden': attrs['aria-hidden'] ?? 'true',
      // The name the caller passed, so the icon can still be found by it (specs, devtools).
      'data-icon': props.icon,
    });
  }
  if (
    import.meta.env.DEV &&
    hasCarbonIcons() &&
    typeof props.icon === 'string' &&
    props.icon.startsWith('i-') &&
    !reported.has(props.icon)
  ) {
    reported.add(props.icon);
    // eslint-disable-next-line no-console
    console.debug(
      `[Icon] no Carbon translation for ${props.icon}; kept Iconify`
    );
  }
  return h('span', {
    ...attrs,
    class: [props.icon, attrs.class],
    'data-icon': props.icon,
  });
};
</script>

<template>
  <component :is="renderIcon" />
</template>
